const PENDING_SCAN_DATA_KEY = 'pendingScanData'
const PENDING_SCAN_IMAGE_KEY = 'pendingScanImage'
const PENDING_SCAN_META_KEY = 'pendingScanMeta'

interface GuestScanWindow extends Window {
	__luckywiseGuestScanActive__?: boolean
}

export interface PendingManualScanData {
	mode: 'manual'
	lotteryId: number
	lotteryName: string
	purchaseDate: string | null
	numbers: number[]
	createdAt: string
}

export interface PendingScannerScanData {
	mode: 'scanner'
	fileName: string
	scanMethod: 'camera' | 'gallery'
	createdAt: string
}

export type PendingScanData = PendingManualScanData | PendingScannerScanData

export interface PendingScanMeta {
	authCompletedTracked?: boolean
	authMethod?: 'google' | 'login' | 'signup' | null
	blurredViewTracked?: boolean
	createAccountClickTracked?: boolean
	guestStartedTracked?: boolean
	loginClickTracked?: boolean
	resultUnlockedTracked?: boolean
	ticketType?: 'manual' | 'scanner' | null
}

const getGuestScanWindow = () => {
	if (!import.meta.client) return null
	return window as GuestScanWindow
}

const markGuestScanSessionActive = () => {
	const guestWindow = getGuestScanWindow()
	if (guestWindow) guestWindow.__luckywiseGuestScanActive__ = true
}

const clearGuestScanSessionFlag = () => {
	const guestWindow = getGuestScanWindow()
	if (guestWindow) delete guestWindow.__luckywiseGuestScanActive__
}

/**
 * Composable that manages temporary scan data for unauthenticated (guest) users in `sessionStorage`.
 * Data is preserved across soft navigations but cleared on page reload (detected via an in-memory flag),
 * preventing stale payloads from being submitted after login.
 *
 * Returned helpers:
 * - `hasPendingScanData` — checks if pending scan or image data exists.
 * - `setPendingScanData / getPendingScanData` — stores/reads the structured ticket payload.
 * - `setPendingScanImage / getPendingScanImage` — stores/reads the base64 ticket image.
 * - `setPendingScanMeta / getPendingScanMeta / mergePendingScanMeta` — manages analytics tracking flags.
 * - `clearPendingScanSession` — removes all pending scan data from sessionStorage.
 * - `ensureFreshGuestSession` — clears stale data left over from a reloaded session.
 * - `fileToDataUrl / dataUrlToFile` — File ↔ data-URL conversion helpers.
 */
export const useGuestScanData = () => {
	const hasPendingScanData = () => {
		if (!import.meta.client) return false
		return Boolean(sessionStorage.getItem(PENDING_SCAN_DATA_KEY) || sessionStorage.getItem(PENDING_SCAN_IMAGE_KEY))
	}

	const setPendingScanData = (payload: PendingScanData) => {
		if (!import.meta.client) return
		sessionStorage.setItem(PENDING_SCAN_DATA_KEY, JSON.stringify(payload))
		markGuestScanSessionActive()
	}

	const getPendingScanData = <T = PendingScanData>() => {
		if (!import.meta.client) return null
		const rawValue = sessionStorage.getItem(PENDING_SCAN_DATA_KEY)
		if (!rawValue) return null

		try {
			return JSON.parse(rawValue) as T
		} catch (error) {
			console.warn('[useGuestScanData] invalid pending scan payload', error)
			sessionStorage.removeItem(PENDING_SCAN_DATA_KEY)
			return null
		}
	}

	const setPendingScanImage = (payload: string) => {
		if (!import.meta.client) return
		sessionStorage.setItem(PENDING_SCAN_IMAGE_KEY, payload)
		markGuestScanSessionActive()
	}

	const getPendingScanImage = () => {
		if (!import.meta.client) return null
		return sessionStorage.getItem(PENDING_SCAN_IMAGE_KEY)
	}

	const clearPendingScanSession = () => {
		if (!import.meta.client) return
		sessionStorage.removeItem(PENDING_SCAN_DATA_KEY)
		sessionStorage.removeItem(PENDING_SCAN_IMAGE_KEY)
		sessionStorage.removeItem(PENDING_SCAN_META_KEY)
		clearGuestScanSessionFlag()
	}

	const getPendingScanMeta = (): PendingScanMeta => {
		if (!import.meta.client) return {}
		const rawValue = sessionStorage.getItem(PENDING_SCAN_META_KEY)
		if (!rawValue) return {}

		try {
			return JSON.parse(rawValue) as PendingScanMeta
		} catch (error) {
			console.warn('[useGuestScanData] invalid pending scan meta', error)
			sessionStorage.removeItem(PENDING_SCAN_META_KEY)
			return {}
		}
	}

	const setPendingScanMeta = (payload: PendingScanMeta) => {
		if (!import.meta.client) return
		sessionStorage.setItem(PENDING_SCAN_META_KEY, JSON.stringify(payload))
		markGuestScanSessionActive()
	}

	const mergePendingScanMeta = (payload: Partial<PendingScanMeta>) => {
		if (!import.meta.client) return
		setPendingScanMeta({
			...getPendingScanMeta(),
			...payload,
		})
	}

	const ensureFreshGuestSession = (isLoggedIn: boolean) => {
		if (!import.meta.client || isLoggedIn) return
		const guestWindow = getGuestScanWindow()

		// If the page was reloaded before login, the in-memory flag is gone while
		// sessionStorage survives. That stale payload must be cleared.
		if (hasPendingScanData() && !guestWindow?.__luckywiseGuestScanActive__) {
			clearPendingScanSession()
		}
	}

	const fileToDataUrl = (file: File) =>
		new Promise<string>((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = () => resolve(String(reader.result))
			reader.onerror = () => reject(reader.error)
			reader.readAsDataURL(file)
		})

	const dataUrlToFile = async (dataUrl: string, fileName: string) => {
		const [header, payload] = dataUrl.split(',', 2)
		if (!header || !payload) {
			throw new Error('Invalid data URL')
		}

		const mimeMatch = header.match(/data:([^;]+)/)
		const mimeType = mimeMatch?.[1] || 'image/jpeg'
		const isBase64 = header.includes(';base64')

		const byteString = isBase64 ? atob(payload) : decodeURIComponent(payload)
		const byteNumbers = new Uint8Array(byteString.length)

		for (let index = 0; index < byteString.length; index += 1) {
			byteNumbers[index] = byteString.charCodeAt(index)
		}

		const blob = new Blob([byteNumbers], { type: mimeType })
		return new File([blob], fileName, { type: mimeType })
	}

	return {
		clearPendingScanSession,
		dataUrlToFile,
		ensureFreshGuestSession,
		fileToDataUrl,
		getPendingScanData,
		getPendingScanImage,
		getPendingScanMeta,
		hasPendingScanData,
		mergePendingScanMeta,
		setPendingScanData,
		setPendingScanImage,
		setPendingScanMeta,
	}
}
